import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  Alert,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit,
  Delete,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import locationService from '../../services/api/locationService';

const LocationsPage = () => {
  const [tabValue, setTabValue] = useState(0);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [error, setError] = useState(null);
  const [selectedCountryId, setSelectedCountryId] = useState('');

  const { control, handleSubmit, formState: { errors }, reset } = useForm();

  useEffect(() => {
    if (tabValue === 0) {
      const loadCountries = async () => {
        setLoading(true);
        try {
          const data = await locationService.getAllCountries();
          setCountries(data);
        } catch (error) {
          console.error('Error fetching countries:', error);
          setError('Error loading countries');
        } finally {
          setLoading(false);
        }
      };
      loadCountries();
    } else if (tabValue === 1) {
      (async () => {
        try {
          if (!countries.length) {
            const countryData = await locationService.getAllCountries();
            setCountries(countryData);
          }
        } catch (err) {
          console.error('Error loading countries for city filter:', err);
        }
      })();
      fetchCities();
    } else if (tabValue === 2) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue]);

  useEffect(() => {
    if (tabValue === 1) {
      fetchCities();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountryId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (tabValue === 0) {
        const data = await locationService.getAllCountries();
        setCountries(data);
      } else if (tabValue === 2) {
        const cityData = await locationService.getAllCities(selectedCountryId || null);
        const allAreas = [];
        for (const city of cityData) {
          try {
            const areaData = await locationService.getAreasByCity(city.id);
            allAreas.push(...areaData);
          } catch (err) {
            console.error(`Error fetching areas for city ${city.id}:`, err);
          }
        }
        setAreas(allAreas);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCities = async () => {
    setLoading(true);
    try {
      const data = await locationService.getAllCities(selectedCountryId || null);
      setCities(data);
    } catch (error) {
      console.error('Error fetching cities:', error);
      setError('Error loading cities');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (item = null) => {
    setSelectedItem(item);
    if (item) {
      reset(item);
    } else {
      reset({
        name: '',
        nameAr: '',
        code: '',
        phoneCode: '',
        isActive: true,
        displayOrder: 0,
        countryId: '',
        cityId: '',
        distanceFromHaram: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedItem(null);
    reset();
  };

  const handleDelete = (item) => {
    setSelectedItem(item);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      if (tabValue === 0) {
        await locationService.deleteCountry(selectedItem.id);
      } else if (tabValue === 1) {
        await locationService.deleteCity(selectedItem.id);
      } else if (tabValue === 2) {
        await locationService.deleteArea(selectedItem.id);
      }
      setDeleteDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error deleting:', error);
      setError('Error deleting item');
    }
  };

  const onSubmit = async (data) => {
    try {
      setError(null);
      const payload = {
        ...data,
        displayOrder:
          data.displayOrder === '' || data.displayOrder === null || data.displayOrder === undefined
            ? 0
            : Number(data.displayOrder) || 0,
        nameAr: tabValue === 0 ? null : (data.nameAr ? data.nameAr.trim() : null),
        phoneCode: data.phoneCode ? data.phoneCode.trim() : null,
        code: data.code ? data.code.trim() : null,
        distanceFromHaram:
          data.distanceFromHaram === '' || data.distanceFromHaram === null || data.distanceFromHaram === undefined
            ? null
            : Number(data.distanceFromHaram),
      };

      if (tabValue === 0) {
        if (selectedItem) {
          await locationService.updateCountry(selectedItem.id, payload);
        } else {
          await locationService.createCountry(payload);
        }
      } else if (tabValue === 1) {
        if (selectedItem) {
          await locationService.updateCity(selectedItem.id, payload);
        } else {
          await locationService.createCity(payload);
        }
      } else if (tabValue === 2) {
        if (selectedItem) {
          await locationService.updateArea(selectedItem.id, payload);
        } else {
          await locationService.createArea(payload);
        }
      }
      handleCloseDialog();
      fetchData();
    } catch (error) {
      setError(error.response?.data?.message || 'Error saving');
    }
  };

  return (
    <Container maxWidth="xl">
      <Typography variant="h4" component="h1" gutterBottom>
        Location Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mt: 2 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Countries" />
          <Tab label="Cities" />
          <Tab label="Areas" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            {tabValue === 1 && (
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Filter by Country</InputLabel>
                <Select
                  value={selectedCountryId}
                  onChange={(e) => setSelectedCountryId(e.target.value)}
                  label="Filter by Country"
                >
                  <MenuItem value="">All Countries</MenuItem>
                  {countries.map((country) => (
                    <MenuItem key={country.id} value={country.id}>
                      {country.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add {tabValue === 0 ? 'Country' : tabValue === 1 ? 'City' : 'Area'}
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {tabValue === 0 && (
                      <>
                        <TableCell>Name</TableCell>
                        <TableCell>Code</TableCell>
                        <TableCell>Phone Code</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </>
                    )}
                    {tabValue === 1 && (
                      <>
                        <TableCell>Name</TableCell>
                        <TableCell>Country</TableCell>
                        <TableCell>Code</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </>
                    )}
                    {tabValue === 2 && (
                      <>
                        <TableCell>Name</TableCell>
                        <TableCell>City</TableCell>
                        <TableCell>Distance from Haram</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tabValue === 0 &&
                    countries.map((country) => (
                      <TableRow key={country.id}>
                        <TableCell>{country.name}</TableCell>
                        <TableCell>{country.code}</TableCell>
                        <TableCell>{country.phoneCode || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip
                            label={country.isActive ? 'Active' : 'Inactive'}
                            color={country.isActive ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => handleOpenDialog(country)}>
                            <Edit />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDelete(country)}>
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  {tabValue === 1 &&
                    cities.map((city) => (
                      <TableRow key={city.id}>
                        <TableCell>{city.name}</TableCell>
                        <TableCell>{city.countryName}</TableCell>
                        <TableCell>{city.code || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip
                            label={city.isActive ? 'Active' : 'Inactive'}
                            color={city.isActive ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => handleOpenDialog(city)}>
                            <Edit />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDelete(city)}>
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  {tabValue === 2 &&
                    areas.map((area) => (
                      <TableRow key={area.id}>
                        <TableCell>{area.name}</TableCell>
                        <TableCell>{area.cityName}</TableCell>
                        <TableCell>
                          {area.distanceFromHaram ? `${area.distanceFromHaram} km` : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={area.isActive ? 'Active' : 'Inactive'}
                            color={area.isActive ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => handleOpenDialog(area)}>
                            <Edit />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDelete(area)}>
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedItem ? 'Edit' : 'Create'} {tabValue === 0 ? 'Country' : tabValue === 1 ? 'City' : 'Area'}
        </DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: 'Name is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Name"
                      error={!!errors.name}
                      helperText={errors.name?.message}
                    />
                  )}
                />
              </Grid>
              {tabValue !== 0 && (
                <Grid item xs={12}>
                  <Controller
                    name="nameAr"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Name (Arabic - Optional)"
                      />
                    )}
                  />
                </Grid>
              )}
              {tabValue === 0 && (
                <>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="code"
                      control={control}
                      rules={{ required: 'Code is required' }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Country Code"
                          error={!!errors.code}
                          helperText={errors.code?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="phoneCode"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Phone Code"
                        />
                      )}
                    />
                  </Grid>
                </>
              )}
              {tabValue === 1 && (
                <Grid item xs={12}>
                  <Controller
                    name="countryId"
                    control={control}
                    rules={{ required: 'Country is required' }}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.countryId}>
                        <InputLabel>Country</InputLabel>
                        <Select {...field} label="Country">
                          {countries.map((country) => (
                            <MenuItem key={country.id} value={country.id}>
                              {country.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.countryId && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                            {errors.countryId.message}
                          </Typography>
                        )}
                      </FormControl>
                    )}
                  />
                </Grid>
              )}
              {tabValue === 2 && (
                <>
                  <Grid item xs={12}>
                    <Controller
                      name="cityId"
                      control={control}
                      rules={{ required: 'City is required' }}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.cityId}>
                          <InputLabel>City</InputLabel>
                          <Select {...field} label="City">
                            {cities.map((city) => (
                              <MenuItem key={city.id} value={city.id}>
                                {city.name}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.cityId && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                              {errors.cityId.message}
                            </Typography>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="distanceFromHaram"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          type="number"
                          label="Distance from Haram (km)"
                        />
                      )}
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <Controller
                  name="isActive"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Status</InputLabel>
                      <Select
                        {...field}
                        value={field.value ? 'true' : 'false'}
                        onChange={(e) => field.onChange(e.target.value === 'true')}
                        label="Status"
                      >
                        <MenuItem value="true">Active</MenuItem>
                        <MenuItem value="false">Inactive</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {selectedItem ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this {tabValue === 0 ? 'country' : tabValue === 1 ? 'city' : 'area'}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LocationsPage;
